/**
 * Removes disallowed characters from a string.
 * Allows letters, numbers, spaces, underscores, and hyphens by default.
 */
export const sanitizeInput = (value, options = { allowUnderscore: true, allowHyphen: true }) => {
  let pattern = "[^a-zA-Z0-9\\s";
  if (options.allowUnderscore) pattern += "_";
  if (options.allowHyphen) pattern += "-";
  pattern += "]";

  const regex = new RegExp(pattern, "g");
  return value.replace(regex, "");
};

export const sanitizeNumberOnly = (value) => {
  if (typeof value !== 'string') value = String(value);
  return value.replace(/[^0-9]/g, '');
};

export const sanitizeNumericInput = (value) => {
  if (typeof value !== 'string') value = String(value);

  // Remove all characters except digits and one decimal point
  const sanitized = value
    .replace(/[^0-9.]/g, '')        // Remove non-numeric and non-dot characters
    .replace(/^\.*/g, '')           // Remove leading dots
    .replace(/(\..*)\./g, '$1');    // Allow only one decimal point

  return sanitized;
};

/**
 * Sanitize HTML content to prevent XSS attacks.
 * Removes script tags, event handlers, and dangerous protocols.
 * @param {string} input - The string to sanitize
 * @returns {string} - Sanitized string safe for display
 */
export const sanitizeHtml = (input) => {
  if (!input || typeof input !== 'string') return '';
  
  let result = input;
  
  // Remove script tags and their content
  result = result.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
  
  // Remove style tags and their content
  result = result.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  
  // Remove event handlers (onclick, onload, onerror, etc.)
  result = result.replace(/\s*on\w+\s*=\s*(['"]).*?\1/gi, '');
  result = result.replace(/\s*on\w+\s*=\s*[^\s>]+/gi, '');
  
  // Remove javascript: protocol
  result = result.replace(/javascript\s*:/gi, '');
  
  // Remove vbscript: protocol
  result = result.replace(/vbscript\s*:/gi, '');
  
  // Remove data: protocol (can be used for XSS)
  result = result.replace(/data\s*:\s*text\/html/gi, '');
  
  // Remove iframe, object, embed tags
  result = result.replace(/<(iframe|object|embed|form)[^>]*>[\s\S]*?<\/\1>/gi, '');
  result = result.replace(/<(iframe|object|embed|form)[^>]*\/?>/gi, '');
  
  return result.trim();
};

/**
 * Sanitize message content - removes all HTML tags for plain text.
 * Use for chat messages and user-generated content.
 * @param {string} input - The message content to sanitize
 * @returns {string} - Plain text with no HTML
 */
export const sanitizeMessage = (input) => {
  if (!input || typeof input !== 'string') return '';
  
  // First apply HTML sanitization
  let result = sanitizeHtml(input);
  
  // Then strip all remaining HTML tags for plain text
  result = result.replace(/<[^>]+>/g, '');
  
  // Decode HTML entities that might be used for obfuscation
  const textarea = document.createElement('textarea');
  textarea.innerHTML = result;
  result = textarea.value;
  
  // Normalize whitespace
  result = result.replace(/\s+/g, ' ').trim();
  
  return result;
};

/**
 * Sanitize user name input - only allows safe characters for names.
 * @param {string} input - The name to sanitize
 * @returns {string} - Sanitized name
 */
export const sanitizeName = (input) => {
  if (!input || typeof input !== 'string') return '';
  
  // Only allow letters, numbers, spaces, hyphens, apostrophes, periods
  return input
    .replace(/<[^>]+>/g, '') // Remove HTML tags
    .replace(/[^a-zA-Z0-9\s\-'.,]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Check if content contains potentially dangerous XSS patterns.
 * @param {string} input - The string to check
 * @returns {boolean} - True if dangerous content detected
 */
export const containsDangerousContent = (input) => {
  if (!input || typeof input !== 'string') return false;
  
  const dangerousPatterns = [
    /<script/i,
    /javascript\s*:/i,
    /vbscript\s*:/i,
    /on\w+\s*=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /data\s*:\s*text\/html/i
  ];
  
  return dangerousPatterns.some(pattern => pattern.test(input));
};


/**
 * Prevents disallowed characters during key press.
 * Allows letters, numbers, spaces, underscores, and hyphens by default.
 */
export const blockSpecialCharKeyPress = (event, options = { allowUnderscore: true, allowHyphen: true }) => {
  let pattern = "^[a-zA-Z0-9\\s";
  if (options.allowUnderscore) pattern += "_";
  if (options.allowHyphen) pattern += "-";
  pattern += "]*$";

  const regex = new RegExp(pattern);
  if (!regex.test(event.key)) {
    event.preventDefault();
  }
};
